import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import api from '../utils/api';

const AuthContext = createContext(null);

const normalizeRole = (role) => (role === 'checker' ? 'viewer' : role);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    if (!api.token) {
      setLoading(false);
      return;
    }

    try {
      const data = await api.getMe();
      setUser(data.user ? { ...data.user, role: normalizeRole(data.user.role) } : null);
    } catch {
      api.setToken(null, false);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (username, password, remember = false) => {
    const data = await api.login(username, password, remember);
    api.setToken(data.token, remember);
    const safeUser = { ...data.user, role: normalizeRole(data.user.role) };
    setUser(safeUser);
    return safeUser;
  };

  const logout = async () => {
    try {
      await api.logout();
    } catch {
      // ignore
    }
    api.setToken(null, false);
    setUser(null);
  };

  const value = useMemo(() => {
    const role = normalizeRole(user?.role);
    return {
      user: user ? { ...user, role } : null,
      loading,
      login,
      logout,
      refreshAuth: checkAuth,
      isAdmin: role === 'admin',
      isManager: role === 'manager',
      isViewer: role === 'viewer',
      canCreateTask: ['admin', 'manager'].includes(role),
      canComment: ['admin', 'manager', 'viewer'].includes(role),
      canManageUsers: role === 'admin',
      canViewUsers: Boolean(role),
      canEditProfile: role === 'admin',
      canViewLogs: Boolean(role),
    };
  }, [user, loading, checkAuth]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
